/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { When } from "react-if";
import { Spacer, Heading, theme, Text, Label } from "@nordcloud/gnui";
import { FormGroup, TransformableInput } from "~/components";
import { StandardForm } from "../components/StandardForm";
import { AccountContacts, AccountParams } from "../types";
import { StepContainer } from "./components/StepContainer";
import {
  URL_AWS_CONSOLE,
  URL_AWS_EXTERNAL_IDS,
  URL_AWS_TEMPLATE,
  AWS_INFO_ONBOARDING,
  AWS_INFO_EXTERNAL_ID,
} from "./constants";
import { trimTransform } from "./utils";
import { schemaCredsAWS } from "./validators";

type Props = {
  onAddAccountSubmit: SubmitHandler<FormData>;
  accountParams: AccountParams;
  additionalSettings: AccountContacts;
  setAdditionalSettings: (settings: AccountContacts) => void;
};

enum FormField {
  OWNER_ID = "ownerId",
  CONTACT_IDS = "contactIds",
  PROVIDER_ID = "providerId",
  EXTERNAL_ID = "externalId",
  ARN = "arn",
}

export function AwsAccountForm({
  onAddAccountSubmit,
  accountParams,
  additionalSettings,
  setAdditionalSettings,
}: Props) {
  const formMethods = useForm({
    resolver: yupResolver(schemaCredsAWS),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = formMethods;

  React.useEffect(() => {
    register(FormField.OWNER_ID);
    register(FormField.CONTACT_IDS);
  }, [register]);

  return (
    <FormProvider {...formMethods}>
      <form id="AWS-cred-form" onSubmit={handleSubmit(onAddAccountSubmit)}>
        <Spacer height={theme.spacing.spacing07} />
        <StandardForm
          accountParams={accountParams}
          additionalSettings={additionalSettings}
          setAdditionalSettings={setAdditionalSettings}
        />
        <When condition={Boolean(accountParams.provider)}>
          <Spacer height={theme.spacing.spacing04} />
          <Heading level={3} marginBottom="spacing03">
            Account Credentials
          </Heading>
          <Text color={theme.color.text.text02}>{AWS_INFO_ONBOARDING}</Text>
        </When>

        <StepContainer counter={1}>
          <Text>
            Sign in to the{" "}
            <a href={URL_AWS_CONSOLE} target="_blank" rel="noreferrer">
              AWS Management Console
            </a>
          </Text>
          <FormGroup error={errors[FormField.PROVIDER_ID]}>
            <Label
              required
              htmlFor={FormField.PROVIDER_ID}
              name="Provide Account ID"
            />
            <TransformableInput
              name={FormField.PROVIDER_ID}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              defaultValue={accountParams.providerId}
              placeholder="e.g. 123456789012"
            />
          </FormGroup>
        </StepContainer>

        <StepContainer counter={2}>
          <Text>In AWS Management Console run CloudFormation</Text>
        </StepContainer>

        <StepContainer counter={3}>
          <Text marginBottom={theme.spacing.spacing01}>
            Create a CloudFormation Stack based on{" "}
            <a href={URL_AWS_TEMPLATE} target="_blank" rel="noreferrer">
              CloudFormation Template
            </a>
          </Text>
          <Text size="sm" color={theme.color.text.text02}>
            {AWS_INFO_EXTERNAL_ID}
          </Text>
          <FormGroup error={errors[FormField.EXTERNAL_ID]}>
            <Label
              required
              htmlFor={FormField.EXTERNAL_ID}
              name="Provide Role External ID"
            />
            <TransformableInput
              name={FormField.EXTERNAL_ID}
              transform={trimTransform}
              control={control}
              placeholder="Provide Role External ID"
              rules={{ required: true }}
            />
          </FormGroup>
          <a href={URL_AWS_EXTERNAL_IDS} target="_blank" rel="noreferrer">
            More information about External IDs
          </a>
        </StepContainer>

        <StepContainer counter={4}>
          <Text marginBottom={theme.spacing.spacing05}>
            In AWS Management Console run IAM and find role by name
            (NordcloudKlarityScannerServiceRole)
          </Text>
          <FormGroup error={errors[FormField.ARN]}>
            <Label required htmlFor={FormField.ARN} name="Provide Role ARN" />
            <TransformableInput
              name={FormField.ARN}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              placeholder="e.g. arn:aws:iam::123456789012:role/NordcloudKlarityScannerServiceRole"
            />
          </FormGroup>
        </StepContainer>
      </form>
    </FormProvider>
  );
}
